import React, { useEffect, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';
import _ from 'underscore';
import { StepHeader } from '@bingads-webui-campaign-react/pmax-common';
import { GeneratedUI } from '@bingads-webui-campaign-react/ui-generator';
import { summaryPageComponentTypes } from '@bingads-webui-campaign-react/summary-page';
import { WIZARD_EVENTS, getCurrentFlow, useWizardService } from '@bingads-webui-react/wizard';
import { STEP_PATHS } from '@bingads-webui-campaign-react/unified-app-campaign-wizard';
import { SummaryShimmer } from '@bingads-webui-campaign-react/perf-max-wizard-north-star';

import { useSummarySchemaData } from './use-summary-schema-data';
import { buildSchema } from './schema';

export const SummaryStepShell = ({
  data,
  onDataChange,
}) => {
  const schemaData = useSummarySchemaData({ data });
  const isLoading = useMemo(() => _.isEmpty(data), [data]);

  const appPlatformString = useMemo(() => {
    if (schemaData.appPlatform && schemaData.appPlatform.data) {
      if (schemaData.appPlatform.data === 'Android') {
        return _TL_('Android mobile app name');
      } else if (schemaData.appPlatform.data === 'iOS') {
        return _TL_('iOS mobile app name');
      } else if (schemaData.appPlatform.data === 'Windows') {
        return _TL_('Windows app name');
      }
    }
    return _TL_('App name');
  }, [schemaData.appPlatform]);

  const { send, state } = useWizardService();

  const currentFlow = getCurrentFlow(state);
  const { wizardData } = state.context;
  const { flow } = wizardData;

  const onAssetsHeaderActionClick = useCallback(() => {
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.ASSETS,
    });
  }, [send]);

  const onTargetingHeaderActionClick = useCallback(() => send(WIZARD_EVENTS.NAVIGATE_TO, {
    stepId: STEP_PATHS.TARGETING,
  }), [send]);

  const onCampaignSettingsHeaderActionClick = useCallback(() =>
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.CAMPAIGN_DETAILS,
    }), [send]);

  const onBudgetSettingsHeaderActionClick = useCallback(() =>
    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.BUDGET,
    }), [send]);

  const options = useMemo(() => ({
    onAssetsHeaderActionClick,
    onTargetingHeaderActionClick,
    onCampaignSettingsHeaderActionClick,
    onBudgetSettingsHeaderActionClick,
    appPlatformString,
    flow,
  }), [onAssetsHeaderActionClick, onTargetingHeaderActionClick, onCampaignSettingsHeaderActionClick, onBudgetSettingsHeaderActionClick, appPlatformString, flow]);

  const schema = buildSchema(options);

  useEffect(() => (
    onDataChange({
      data,
      isValid: true,
    })
  ), [data, onDataChange]);

  return (
    <div className="summary-step-container" >
      <StepHeader
        title={_TL_('Summary')}
        description={_TL_('Review your campaign details and select Go live when you\'re ready.')}
      />
      {isLoading ?
        <SummaryShimmer currentSteps={currentFlow} /> :
        <GeneratedUI schema={schema} customComponentTypes={summaryPageComponentTypes} {...schemaData} />
      }
    </div>
  );
};

SummaryStepShell.propTypes = {
  data: PropTypes.shape({}),
  onDataChange: PropTypes.func.isRequired,
};

SummaryStepShell.defaultProps = {
  data: {},
};
